/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/require-default-props */
import React, { useState, useRef } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { appsApi } from '../../api';

import { useAlerts } from '../context';

import CopyButton from '../common/CopyButton';
import ConfirmationModal from '../common/ConfirmationModal';
import ConfirmPasswordActionForm from '../auth/ConfirmPasswordActionForm';

function ClientSecretFieldWrapper({ app, credentials, onGenerate, readOnly, disableGenerate }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const inputRef = useRef(null);
  const { setAlert } = useAlerts();
  const { t } = useTranslation();

  // eslint-disable-next-line consistent-return
  const generate = async (regenApp) => {
    if (regenApp) {
      try {
        const response = await appsApi.regenerateClientSecret(regenApp);
        // eslint-disable-next-line no-param-reassign
        credentials.client_secret = response.client_secret;
        if (onGenerate) onGenerate(credentials);
        setShowConfirm(false);
        return credentials;
      } catch (error) {
        setAlert({ message: error, variant: 'warning' });
      }
    } else {
      setShowConfirm(false);
    }
  };

  return (
    <>
      <Form.Group
        className={classNames('client_secret', readOnly && 'readonly', 'mt-3')}
        controlId={`clientSecretGenForm.${uuid()}.client_secret`}
      >
        <Form.Label>{t('common:applications.client_secret')}</Form.Label>
        <InputGroup className="align-items-center">
          <Form.Control
            tabIndex={0}
            autoComplete="new-password"
            type={credentials.client_secret ? 'text' : 'password'}
            name="client_secret"
            className={credentials.client_secret ? 'text-ellipsis w-50' : undefined}
            value={
              !credentials.client_secret
                ? '•••••••••••••••••••••••••••••••'
                : credentials.client_secret
            }
            readOnly
          />
          <input
            className="visually-hidden"
            type="text"
            name="client_secret_hidden"
            value={credentials.client_secret}
            ref={inputRef}
            readOnly
          />
          {credentials.client_secret && document.queryCommandSupported('copy') && (
            <CopyButton inputRef={inputRef} variant="link" placement="top" className="p-1 ms-2" />
          )}
          {!disableGenerate && (
            <Button
              className="generate ms-2 rounded-1"
              variant="primary"
              size="sm"
              onClick={() => setShowConfirm(true)}
            >
              {t('common:buttons.regenerate')}
            </Button>
          )}
        </InputGroup>
      </Form.Group>

      {showConfirm && (
        <ConfirmationModal
          buttonDisabled
          show={showConfirm}
          setShow={setShowConfirm}
          data={app}
          title={t('applications.regenerate.title')}
          renderMessageComponent={({ setDisabled }) => (
            <>
              <p className="text-lg mb-4">{t('applications.regenerate.description')}</p>
              <p className="text-info mb-4">{t('applications.regenerate.note')}</p>
              <ConfirmPasswordActionForm onPasswordConfirm={(isValid) => setDisabled(!isValid)} />
            </>
          )}
          buttonLabel={t('common:buttons.regenerate')}
          handleSubmit={generate}
        />
      )}
    </>
  );
}

ClientSecretFieldWrapper.propTypes = {
  /**
   * The applicatioin id
   */
  app: PropTypes.number,
  /**
   * The application credentials
   */
  credentials: PropTypes.object,
  /**
   * The function that is called when the secret is generated.
   */
  onGenerate: PropTypes.func,
  /**
   * The read only boolean
   */
  readOnly: PropTypes.bool,
  /**
   * The disable generate button boolean
   */
  disableGenerate: PropTypes.bool,
};

function ClientIdFieldWrapper({ credentials, readOnly }) {
  const inputRef = useRef(null);
  const { t } = useTranslation();
  return (
    <Form.Group
      className={classNames('client_id', readOnly && 'readonly')}
      controlId={`clientSecretGenForm.${uuid()}.client_id`}
    >
      <Form.Label>{t('common:applications.client_id')}</Form.Label>
      <InputGroup>
        <Form.Control
          tabIndex={0}
          autoComplete="new-password"
          type="text"
          name="client_id"
          value={credentials.client_id}
          readOnly
        />
        <input
          className="visually-hidden"
          type="text"
          name="client_id_hidden"
          value={credentials.client_id}
          ref={inputRef}
          readOnly
        />

        {document.queryCommandSupported('copy') && (
          <CopyButton inputRef={inputRef} variant="link" placement="top" className="p-1 ms-2" />
        )}
      </InputGroup>
    </Form.Group>
  );
}

ClientIdFieldWrapper.propTypes = {
  /**
   * The generated application client credentials
   */
  credentials: PropTypes.object,
  /**
   * The read only boolean
   */
  readOnly: PropTypes.bool,
};

export const ClientSecretField = ClientSecretFieldWrapper;
export const ClientIdField = ClientIdFieldWrapper;
